<template>
  <div>
    <!-- Table Component -->
    <div class="bg-white shadow overflow-hidden rounded-lg">
      <div class="relative w-full overflow-x-auto overflow-y-auto table-container">
        <table class="min-w-full w-full" :style="{ 'table-layout': fixedLayout ? 'fixed' : 'auto' }">
          <thead class="sticky top-0 z-10 bg-gray-50 shadow-sm">
            <tr>
              <!-- Default header generation -->
              <slot name="header">
              <template v-if="!$slots.header && headers.length > 0">
                <th
                  v-for="(header, index) in headers"
                  :key="index"
                  :id="header.id ? `${idPrefix}${header.id}` : `table-header-${index}`"
                  scope="col"
                  class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  :class="[header.width ? header.width : '']"
                >
                  {{ header.label }}
                </th>
              </template>
              <!-- Custom header slot -->
              </slot>
            </tr>
          </thead>
          <tbody>
            <!-- Skeleton loading rows -->
            <template v-if="loading">
              <tr
                v-for="n in skeletonCount"
                :key="`skeleton-${n}`"
                class="animate-pulse"
              >
                <slot name="skeleton" :index="n">
                  <td
                    v-for="(header, index) in headers"
                    :key="`skeleton-col-${index}`"
                    class="px-4 py-4"
                  >
                    <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div class="h-4 bg-gray-200 rounded w-1/2"></div>
                  </td>
                </slot>
              </tr>
            </template>

            <!-- Actual data rows -->
            <template v-else>
              <template v-if="items.length > 0">
                <tr
                  v-for="(item, index) in items"
                  :key="getItemKey(item, index)"
                  class="hover:bg-gray-50"
                  @click="onRowClicked(item)"
                  :class="{ 'cursor-pointer': clickable }"
                  :id="
                    idPrefix
                      ? `${idPrefix}.row${getItemKey(item, index)}`
                      : `table-row-${getItemKey(item, index)}`
                  "
                >
                  <slot name="item" :item="item" :index="index">
                    <td
                      v-for="(header, headerIndex) in headers"
                      :key="headerIndex"
                      class="px-4 py-4 text-sm text-gray-900"
                    >
                      <div class="w-full break-words">
                        {{ getItemValue(item, header.key) }}
                      </div>
                    </td>
                  </slot>
                </tr>
              </template>

              <!-- Empty state when no results -->
              <tr v-else>
                <td
                  :colspan="headers.length || 1"
                  class="px-6 py-4 text-center text-sm text-gray-500"
                >
                  <slot name="empty"> No items found </slot>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="showPagination && !loading" class="flex justify-center mt-4">
      <nav
        class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
        aria-label="Pagination"
      >
        <!-- Previous page -->
        <button
          :id="idPrefix ? `${idPrefix}previouspage` : 'table-previous-page'"
          @click="pageChange(currentPage > 1 ? currentPage - 1 : 1)"
          :disabled="currentPage <= 1"
          class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage <= 1 }"
        >
          <span class="sr-only">Previous</span>
          <i class="fas fa-chevron-left"></i>
        </button>

        <!-- Page numbers -->
        <template v-for="page in displayedPages">
          <span
            v-if="page === '...'"
            :key="'ellipsis-' + page"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
            >...</span
          >
          <button
            v-else
            :key="page"
            :id="
              idPrefix
                ? `${idPrefix}gotopage${page}`
                : `table-goto-page-${page}`
            "
            @click="pageChange(page)"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium"
            :class="
              currentPage === page
                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:bg-gray-50'
            "
          >
            {{ page }}
          </button>
        </template>

        <!-- Next page -->
        <button
          :id="idPrefix ? `${idPrefix}nextpage` : 'table-next-page'"
          @click="
            pageChange(currentPage < totalPages ? currentPage + 1 : totalPages)
          "
          :disabled="currentPage >= totalPages"
          class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          :class="{
            'opacity-50 cursor-not-allowed': currentPage >= totalPages,
          }"
        >
          <span class="sr-only">Next</span>
          <i class="fas fa-chevron-right"></i>
        </button>
      </nav>
    </div>
  </div>
</template>

<script>
export default {
  name: "BaseTable",

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    headers: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    skeletonCount: {
      type: Number,
      default: 5,
    },

    fixedLayout: {
      type: Boolean,
      default: true,
    },

    itemKey: {
      type: String,
      default: "id",
    },

    pagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        totalItems: 0,
        perPage: 10,
        enabled: true,
      }),
    },

    idPrefix: {
      type: String,
      default: "",
    },

    clickable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentPage: this.pagination.currentPage || 1,
      totalItems: this.pagination.totalItems || this.items.length,
      perPage: this.pagination.perPage || 10,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.perPage);
    },

    showPagination() {
      return this.pagination.enabled !== false;
    },

    displayedPages() {
      const totalPages = this.totalPages;
      const currentPage = this.currentPage;
      const pages = [];

      // Always show first page
      pages.push(1);

      // Logic for displaying pages with ellipsis for large ranges
      if (totalPages <= 7) {
        // Less than 7 pages, show all
        for (let i = 2; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        // More than 7 pages, use ellipsis
        if (currentPage > 3) {
          pages.push("...");
        }

        // Show current page and pages around it
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }

        if (currentPage < totalPages - 2) {
          pages.push("...");
        }
      }

      // Always show last page if more than 1 page
      if (totalPages > 1) {
        pages.push(totalPages);
      }

      return pages;
    },
  },

  watch: {
    "pagination.currentPage"(newVal) {
      if (newVal !== undefined) {
        this.currentPage = newVal;
      }
    },
    "pagination.totalItems"(newVal) {
      if (newVal !== undefined) {
        this.totalItems = newVal;
      }
    },
    "pagination.perPage"(newVal) {
      if (newVal !== undefined) {
        this.perPage = newVal;
      }
    },
    items: {
      handler(newItems) {
        if (!this.pagination.totalItems) {
          this.totalItems = newItems.length;
        }
      },
      immediate: true,
    },
  },

  methods: {
    // Get a unique key for each row
    getItemKey(item, index) {
      return item[this.itemKey] || index;
    },

    // Extract value from nested object path (supports dot notation)
    getItemValue(item, key) {
      if (!key || typeof key !== "string") return "";

      return key.split(".").reduce((obj, property) => {
        return obj && obj[property] !== undefined ? obj[property] : "";
      }, item);
    },

    // Handle page change
    pageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.$emit("page-change", page);
      }
    },

    // Row Clicked
    onRowClicked (item) {
      this.clickable && this.$emit('rowClicked', item);
    },
  },
};
</script>

<style scoped>
.table-container {
  max-height: 700px;
}

.table-container tr {
  border-bottom: 1px solid #e5e7eb;
}
</style>
